import { useEffect, useState } from "react";

/*  INCOMING TRANSMISSION — brand entry hook.
    Fires on first visit, auto-dismisses after 7s or on any button tap.
    Writes a localStorage flag so returning visitors skip straight to the
    homepage. Pure CSS + minimal state — zero deps beyond React.

    Visual: near-black backdrop + CRT scanline sweep + typewriter stagger.
*/
const SEEN_KEY = "wm_transmission_seen_v1";

export const TransmissionIntro = ({ onDone }) => {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) !== "1"; } catch { return true; }
  });

  useEffect(() => {
    if (!visible) { onDone?.(); return; }
    const t = setTimeout(() => dismiss("timeout"), 7200);
    // If any other tab/component sets the seen key, dismiss immediately
    const onStorage = (e) => {
      if (e.key === SEEN_KEY && e.newValue === "1") dismiss("storage");
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearTimeout(t);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = (reason) => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
    setVisible(false);
    onDone?.(reason);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black overflow-hidden flex items-center justify-center transmission-root"
      role="dialog" aria-label="Incoming transmission"
      data-testid="transmission-intro">
      {/* Scanline sweep */}
      <div className="transmission-scanline" aria-hidden="true" />
      {/* Grain overlay */}
      <div className="transmission-grain" aria-hidden="true" />

      <div className="relative z-10 px-6 max-w-2xl text-center">
        <div className="text-[10px] tracking-[0.4em] text-[#8b0000] transmission-pulse mb-4"
          data-testid="transmission-header">
          ● INCOMING TRANSMISSION · 01:17 AM
        </div>
        <div className="font-display text-3xl sm:text-5xl text-[#f4f0ec] leading-tight mb-8 transmission-line transmission-line-1">
          Something isn't right.
        </div>
        <div className="font-display text-2xl sm:text-4xl text-[#d4af37] leading-tight mb-4 transmission-line transmission-line-2">
          Three people are lying.
        </div>
        <div className="font-display text-2xl sm:text-4xl text-[#f4f0ec] leading-tight mb-10 transmission-line transmission-line-3">
          One of them is already in your crew.
        </div>
        <div className="text-sm text-[#a39797] mb-8 transmission-line transmission-line-4">
          A file just surfaced that was never supposed to exist.
        </div>

        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap transmission-line transmission-line-5">
          <button onClick={() => dismiss("investigate")}
            className="px-5 sm:px-6 py-3 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors tracking-[0.3em] text-xs sm:text-sm"
            data-testid="transmission-investigate">
            [ INVESTIGATE ]
          </button>
          <button onClick={() => dismiss("accuse")}
            className="px-5 sm:px-6 py-3 border border-[#8b0000] text-[#f4f0ec] hover:bg-[#8b0000] transition-colors tracking-[0.3em] text-xs sm:text-sm"
            data-testid="transmission-accuse">
            [ ACCUSE ]
          </button>
          <button onClick={() => dismiss("keep_quiet")}
            className="px-5 sm:px-6 py-3 border border-[#2d2424] text-[#a39797] hover:border-[#5a5050] hover:text-[#f4f0ec] transition-colors tracking-[0.3em] text-xs sm:text-sm"
            data-testid="transmission-keep-quiet">
            [ KEEP QUIET ]
          </button>
        </div>

        <div className="text-[10px] tracking-[0.3em] text-[#5a5050] mt-10">
          wasntme.party · the internet's live mystery arena
        </div>
      </div>
      {/* Explicit SKIP for returning visitors + test agents — top-right corner */}
      <button
        onClick={() => dismiss("skip")}
        className="absolute top-4 right-4 text-[10px] tracking-[0.3em] text-[#5a5050] hover:text-[#d4af37] transition-colors px-3 py-1.5 border border-[#2d2424] hover:border-[#d4af37]"
        data-testid="transmission-skip"
        aria-label="Skip intro"
      >
        SKIP →
      </button>
    </div>
  );
};

export default TransmissionIntro;
